/**
 * Plain-English slot/product formatters for warehouse pallets.
 * Single source of truth — never inline `${side}-${level}${row}-${col}` again.
 */

const SIDE_LABEL: Record<string, string> = {
  A: "Left Side",
  B: "Right Side",
};

const LEVEL_LABEL: Record<string, string> = {
  upper: "Top Rack",
  lower: "Bottom Rack",
};

type SlotInput = {
  side: string | null | undefined;
  level: string | null | undefined;
  row: number | null | undefined;
  col: number | null | undefined;
};

export function formatSlotLabel(p: SlotInput | null | undefined): string {
  if (!p) return "—";
  // If nothing about the slot is known, render a single explicit "Unslotted"
  // label rather than the noisy "— · Row ? · — · Slot ?" placeholder string.
  if (p.side == null && p.level == null && p.row == null && p.col == null) {
    return "Unslotted";
  }
  const side = SIDE_LABEL[p.side ?? ""] ?? (p.side ? `Side ${p.side}` : "—");
  const level = LEVEL_LABEL[p.level ?? ""] ?? p.level ?? "—";
  return `${side} · Row ${p.row ?? "?"} · ${level} · Slot ${p.col ?? "?"}`;
}

/**
 * Compact slot code (e.g. "A-U3-2") for pick lists, search snippets, exports.
 * Returns the literal string "Unslotted" when ANY of side/level/row/col is
 * missing — never produces ad-hoc "?-?-null-null" or "--R-C" placeholders.
 */
export function formatSlotShort(p: SlotInput | null | undefined): string {
  if (!p) return "Unslotted";
  if (p.side == null || p.level == null || p.row == null || p.col == null) {
    return "Unslotted";
  }
  const lvl = String(p.level).charAt(0).toUpperCase();
  return `${p.side}-${lvl}${p.row}-${p.col}`;
}

type ProductInput = {
  brand?: string | null;
  oz?: number | null;
  sku?: string | null;
};

/**
 * Headline product label for a pallet — brand + size when available,
 * falls back to SKU. Used as the primary line on count rows.
 */
export function formatPalletProduct(p: ProductInput | null | undefined): string {
  if (!p) return "—";
  const brand = p.brand?.trim() ?? "";
  const sizeToken = p.oz != null ? `${p.oz}oz` : "";
  // Skip the size suffix when the brand already mentions it
  // (e.g. brand="Roasted Eel 9oz" + oz=9 → "Roasted Eel 9oz", not "Roasted Eel 9oz 9oz").
  const brandHasSize = !!sizeToken && !!brand && new RegExp(`\\b${p.oz}\\s*oz\\b`, "i").test(brand);
  if (brand && sizeToken && !brandHasSize) return `${brand} ${sizeToken}`;
  if (brand) return brand;
  if (sizeToken) return sizeToken;
  return p.sku ?? "—";
}
